import React, { useState } from 'react';
import { Bell, Users, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { format } from 'date-fns';

type PartnerLinkNotificationProps = {
  clientProfiles: ClientProfile[];
  onAcceptRequest: (requestingClient: ClientProfile) => void;
  onDeclineRequest: (requestingClient: ClientProfile) => void;
};

export function PartnerLinkNotification({
  clientProfiles,
  onAcceptRequest,
  onDeclineRequest,
}: PartnerLinkNotificationProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Find all clients with pending partner link requests
  const pendingRequests = clientProfiles.filter(
    client => client.partnerLinkRequestClient,
  );

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full bg-white/10 text-primary-dark hover:bg-white/20"
        >
          <Bell className="h-5 w-5" />
          {pendingRequests.length > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
              {pendingRequests.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-96 rounded-xl p-0"
        align="end"
        side="bottom"
        sideOffset={8}
      >
        <div className="border-b border-gray-100 p-4">
          <h3 className="font-semibold text-gray-900">Partner Link Requests</h3>
          <p className="text-sm text-gray-500">
            {pendingRequests.length} pending request
            {pendingRequests.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {pendingRequests.map(client => (
            <PartnerRequestItem
              key={client.clientId}
              client={client}
              onAccept={() => {
                onAcceptRequest(client);
                setIsOpen(false);
              }}
              onDecline={() => {
                onDeclineRequest(client);
                setIsOpen(false);
              }}
            />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type PartnerRequestItemProps = {
  client: ClientProfile;
  onAccept: () => void;
  onDecline: () => void;
};

function PartnerRequestItem({
  client,
  onAccept,
  onDecline,
}: PartnerRequestItemProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const requestingClient = client.partnerLinkRequestClient!;

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      await onAccept();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    try {
      await onDecline();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="border-b border-gray-50 p-4 last:border-b-0">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
          <Users className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-grow space-y-1">
          <p className="text-sm font-medium text-gray-900">
            <span className="font-semibold">
              {requestingClient.clientFirstName}{' '}
              {requestingClient.clientLastName}
            </span>{' '}
            wants to link accounts with{' '}
            <span className="font-semibold">
              {client.clientFirstName} {client.clientLastName}
            </span>
          </p>
          <p className="text-xs text-gray-500">
            Requested on {format(client.clientAddedDateTime, 'MMM d, yyyy')}
          </p>
          <p className="text-xs text-gray-500">
            Advisor: {client.clientAdvisor.advisorFirstName}{' '}
            {client.clientAdvisor.advisorLastName}
          </p>
        </div>
      </div>
      <div className="mt-3 flex space-x-2">
        <Button
          size="sm"
          onClick={handleAccept}
          disabled={isProcessing}
          className="flex-1 bg-green-500 text-white hover:bg-green-600"
        >
          <Check className="mr-1 h-4 w-4" />
          Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDecline}
          disabled={isProcessing}
          className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
        >
          <X className="mr-1 h-4 w-4" />
          Decline
        </Button>
      </div>
    </div>
  );
}
